import { useState } from 'react';
import { KeyValueList, PageHeader, Panel, Toolbar } from '../components/Page';
import { SelectField } from '../components/Form';
import { Chip } from '../components/Chips';
import { CardSkeleton, EmptyState, ErrorState, TableSkeleton } from '../components/Feedback';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../lib/auth';
import { GatewayRole } from '../lib/enums';
import { platform } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime, formatRelative } from '../lib/format';
import type { PlatformDeliveryDto } from '../lib/types';

type DeliveryColumn = 'type' | 'version' | 'verified' | 'outcome' | 'received';

function deliverySortValue(d: PlatformDeliveryDto, key: DeliveryColumn): string | number {
  switch (key) {
    case 'type':
      return d.deliveryType;
    case 'version':
      return d.dataVersion ?? 0;
    case 'verified':
      return d.hashVerified === true ? 2 : d.hashVerified === false ? 0 : 1;
    case 'outcome':
      return d.status;
    case 'received':
      return d.receivedUtc;
    default:
      return '';
  }
}

const TYPE_OPTIONS = [
  { value: 'REGISTRATIONCONFIGSNAPSHOT', label: 'Registration configuration snapshot' },
  { value: 'ENVIRONMENTASSIGNMENTCHANGED', label: 'Environment assignment changed' },
  { value: 'LICENSEPURCHASESTATUSCHANGED', label: 'Licence purchase status changed' },
  { value: 'LICENSECATALOGCHANGED', label: 'Licence catalogue changed' },
  { value: 'DELIVERYMETHODCHANGED', label: 'Delivery method changed' },
  { value: 'BILLINGCONNECTIONCHANGED', label: 'Billing connection changed' },
  { value: 'CONNECTIONSTATUSCHANGED', label: 'Connection status changed' },
  { value: 'HANDSHAKEINVOICECAPABILITYCHANGED', label: 'Invoice capability changed' },
  { value: 'VENDORPAYMENTNOTIFICATION', label: 'Vendor payment notification' },
];

const STATUS_OPTIONS = [
  { value: 'Applied', label: 'Applied' },
  { value: 'Ignored', label: 'Ignored — older than what is applied' },
  { value: 'Failed', label: 'Failed — will be re-served' },
  { value: 'Unverified', label: 'Not applied — payload could not be verified' },
  { value: 'Deferred', label: 'Deferred — this version cannot apply it' },
  { value: 'Rejected', label: 'Rejected — not for this gateway' },
];

function statusTone(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  switch (status) {
    case 'Applied':
      return 'good';
    case 'Failed':
    case 'Rejected':
    case 'Unverified':
      return 'bad';
    case 'Ignored':
    case 'Deferred':
      return 'warn';
    default:
      return 'neutral';
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="kv">
      <dt className="kv-label">{label}</dt>
      <dd className="kv-value">{children}</dd>
    </div>
  );
}

/**
 * THIS COMPANY'S STANDING WITH THE iERP PLATFORM.
 *
 * Business Central shows this on its connection status and diagnostics pages; the gateway had
 * nothing. What is here: where the registration is ASSIGNED to run (and whether the move was
 * acknowledged), the processing credential it holds and when it was last repaired, the state of
 * the delivery lane, the platform's own registration health, and every platform message received —
 * with whether its hash verified and whether it was applied, ignored or rejected.
 *
 * The three actions talk to the platform on the company's behalf and are administrator-only.
 * None of them needs anything typed in.
 */
export function PlatformPage(): JSX.Element {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = (user?.role ?? GatewayRole.Auditor) >= GatewayRole.Admin;

  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [busy, setBusy] = useState<string | null>(null);

  const state = useAsync((signal) => platform.state(signal), []);
  const deliveries = useAsync(
    (signal) => platform.deliveries({ type: type || null, status: status || null, page, pageSize }, signal),
    [type, status, page, pageSize],
  );

  const table = useSortableTable<DeliveryColumn>({
    storageKey: 'platformDeliveriesColumnWidths',
    columns: {
      type: { minWidth: 160 },
      version: { minWidth: 130 },
      verified: { minWidth: 150 },
      outcome: { minWidth: 140 },
      received: { minWidth: 140 },
    },
  });

  const run = async (name: string, action: () => Promise<{ success: boolean; message: string | null }>) => {
    setBusy(name);
    try {
      const outcome = await action();
      toast.success(outcome.message ?? `${name} completed.`);
      state.reload();
    } catch (caught) {
      toast.error(`${name} did not complete`, errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const s = state.data;

  return (
    <>
      <PageHeader
        title="Platform"
        description="Where this company's iERP registration is served, the credential it holds, the delivery lane, and every message the platform sent."
        actions={
          isAdmin ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy !== null}
                onClick={() => void run('Assignment refresh', () => platform.refreshAssignment())}
              >
                {busy === 'Assignment refresh' ? 'Refreshing…' : 'Refresh assignment'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy !== null}
                onClick={() => void run('Credential repair', () => platform.repairCredential())}
              >
                {busy === 'Credential repair' ? 'Repairing…' : 'Repair credential'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy !== null}
                onClick={() => void run('Health check', () => platform.refreshHealth())}
              >
                {busy === 'Health check' ? 'Reading…' : 'Read health'}
              </button>
            </>
          ) : undefined
        }
      />

      <Panel title="Environment assignment" description="Which iERP platform environment serves this registration, and whether the platform has our acknowledgement.">
        {state.loading && !s ? (
          <CardSkeleton />
        ) : state.error ? (
          <ErrorState message="The platform state could not be read." detail={state.error} onRetry={state.reload} />
        ) : s ? (
          <>
            <Toolbar>
              <Chip tone={s.assignedEnvironment ? 'good' : 'warn'}>
                {s.assignedEnvironment ? `Assigned: ${s.assignedEnvironment}` : 'Assignment not read yet'}
              </Chip>
              {s.assignmentVersion > 0 ? (
                <Chip tone={s.assignmentAcknowledgedVersion >= s.assignmentVersion ? 'good' : 'warn'}>
                  {s.assignmentAcknowledgedVersion >= s.assignmentVersion
                    ? `version ${s.assignmentVersion} acknowledged`
                    : `version ${s.assignmentVersion} not yet acknowledged`}
                </Chip>
              ) : null}
              {s.temporaryOverride ? <Chip tone="warn">temporary override</Chip> : null}
            </Toolbar>
            <KeyValueList columns={2}>
              <Row label="Platform address">
                <span className="mono">{s.effectiveBaseUrl}</span>
                {s.assignedBaseUrl ? <span className="cell-sub">assigned by the platform (overrides the configured default)</span> : <span className="cell-sub">configured default</span>}
              </Row>
              <Row label="Home environment">{s.homeEnvironment ?? '—'}</Row>
              <Row label="Reason">{s.assignmentReason ?? '—'}</Row>
              <Row label="Expires">{s.assignmentExpiresUtc ? formatDateTime(s.assignmentExpiresUtc) : '—'}</Row>
              <Row label="Last read">{s.assignmentRefreshedUtc ? formatRelative(s.assignmentRefreshedUtc) : 'never'}</Row>
            </KeyValueList>
            {s.assignmentMessage ? <p className="muted">{s.assignmentMessage}</p> : null}
          </>
        ) : null}
      </Panel>

      {s ? (
        <>
          <Panel title="Credential and registration" description="The processing credential this gateway exchanges for tokens, and what the Registration Server last said about the registration.">
            <KeyValueList columns={2}>
              <Row label="Processing credential">
                {s.currentAccessKeyId ? <span className="mono">{s.currentAccessKeyId}</span> : <Chip tone="warn">none issued</Chip>}
              </Row>
              <Row label="Last credential repair">
                {s.lastCredentialRepairUtc ? formatRelative(s.lastCredentialRepairUtc) : 'never'}
                {s.lastCredentialRepairMessage ? <span className="cell-sub">{s.lastCredentialRepairMessage}</span> : null}
              </Row>
              <Row label="Registration Server status">
                {s.serverRegistrationStatus ?? '—'}
                {s.serverValidated ? <Chip tone="good">validated</Chip> : null}
              </Row>
              <Row label="Client status">{s.clientStatus ?? '—'}</Row>
              <Row label="Configuration snapshot">
                {s.configAppliedUtc ? `version ${s.configDataVersion}, applied ${formatRelative(s.configAppliedUtc)}` : 'none received yet'}
              </Row>
              <Row label="Catalogue last changed">{s.catalogChangedUtc ? formatRelative(s.catalogChangedUtc) : '—'}</Row>
              <Row label="Billing connection">
                {s.billingConnectionId ? <span className="mono">{s.billingConnectionId}</span> : '—'}
                {s.billingConnectionStatus ? <Chip tone={s.billingConnectionStatus === 'CONFLICT' ? 'bad' : 'neutral'}>{s.billingConnectionStatus}</Chip> : null}
              </Row>
            </KeyValueList>
          </Panel>

          <Panel title="Delivery lane" description="Platform messages and partner invoices arrive on one pull. The server may ask for another method; this gateway can only pull, and re-asserts it.">
            <Toolbar>
              <Chip tone={s.deliveryLaneConfigured ? 'good' : 'warn'}>{s.deliveryLaneConfigured ? 'Scheduled Pull configured' : 'Not configured yet'}</Chip>
              {s.lastPullUtc ? <Chip tone="neutral">last pull {formatRelative(s.lastPullUtc)}</Chip> : <Chip tone="warn">never pulled</Chip>}
              {s.serverRequestedDeliveryMethod && s.serverRequestedDeliveryMethod.toUpperCase().replace(/[ _-]/g, '') !== 'SCHEDULEDPULL' ? (
                <Chip tone="warn">server requested {s.serverRequestedDeliveryMethod}</Chip>
              ) : null}
            </Toolbar>
            {s.lastPullMessage ? <p className="muted">{s.lastPullMessage}</p> : null}
          </Panel>

          <Panel title="Registration health" description="The platform's own view, verbatim. Read health asks for it now.">
            {s.health ? (
              <>
                <p className="muted">Read {s.healthCheckedUtc ? formatRelative(s.healthCheckedUtc) : ''}.</p>
                <pre className="assistant-pre">{JSON.stringify(s.health, null, 2)}</pre>
              </>
            ) : (
              <p className="muted">Not read yet.</p>
            )}
          </Panel>
        </>
      ) : null}

      <Panel title="Platform messages" description="Every message the platform delivered to this company: what it was, whether it verified, and what happened to it.">
        <Toolbar>
          <SelectField label="Type" value={type} onChange={(v) => { setType(v); setPage(1); }} options={TYPE_OPTIONS} placeholder="All types" className="toolbar-grow" />
          <SelectField label="Outcome" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUS_OPTIONS} placeholder="All outcomes" className="toolbar-grow" />
        </Toolbar>

        {deliveries.loading && !deliveries.data ? (
          <TableSkeleton rows={6} columns={5} />
        ) : deliveries.error ? (
          <ErrorState message="Platform messages could not be read." detail={deliveries.error} onRetry={deliveries.reload} />
        ) : !deliveries.data || deliveries.data.items.length === 0 ? (
          <EmptyState title="Nothing received yet" description="Platform messages appear here as the delivery lane pulls them." />
        ) : (
          <>
            <TableWrap busy={deliveries.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={table.columnStyle('type')} />
                <col style={table.columnStyle('version')} />
                <col style={table.columnStyle('verified')} />
                <col style={table.columnStyle('outcome')} />
                <col style={table.columnStyle('received')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Type"
                    sortKey="type"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('type')}
                    onResizeStart={table.beginResize('type')}
                  />
                  <SortableTh
                    label="Version"
                    sortKey="version"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('version')}
                    onResizeStart={table.beginResize('version')}
                  />
                  <SortableTh
                    label="Verified"
                    sortKey="verified"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('verified')}
                    onResizeStart={table.beginResize('verified')}
                  />
                  <SortableTh
                    label="Outcome"
                    sortKey="outcome"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('outcome')}
                    onResizeStart={table.beginResize('outcome')}
                  />
                  <SortableTh
                    label="Received"
                    sortKey="received"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('received')}
                    onResizeStart={table.beginResize('received')}
                  />
                </tr>
              </thead>
              <tbody>
                {table.sort(deliveries.data.items, deliverySortValue).map((d) => (
                  <tr key={d.id}>
                    <td>
                      <span className="cell-strong">{d.deliveryType}</span>
                      <span className="cell-sub mono">{d.deliveryId}</span>
                    </td>
                    <td>
                      {d.dataVersion ?? '—'}
                      <span className="cell-sub">{d.sourceModifiedUtc ? `source ${formatDateTime(d.sourceModifiedUtc)}` : ''}</span>
                    </td>
                    <td>
                      {d.hashVerified === true ? <Chip tone="good">hash verified</Chip>
                        : d.hashVerified === false ? <Chip tone="warn">hash not reproducible</Chip>
                        : <Chip tone="neutral">no hash</Chip>}
                    </td>
                    <td>
                      <Chip tone={statusTone(d.status)}>{d.status}</Chip>
                      {d.message ? <span className="cell-sub">{d.message}</span> : null}
                    </td>
                    <td>
                      {formatRelative(d.receivedUtc)}
                      <span className="cell-sub">{d.acknowledged ? 'acknowledged' : 'acknowledgement pending'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <Pagination
              page={deliveries.data.page}
              pageSize={deliveries.data.pageSize}
              totalCount={deliveries.data.totalCount}
              totalPages={deliveries.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          </>
        )}
      </Panel>
    </>
  );
}
